'use client';

import React from 'react';
import styles from './ProgressBar.module.css';

export interface ProgressBarProps {
  /**
   * Progress value (0-100)
   */
  value: number;
  /**
   * Height of the progress bar in pixels
   * @default 8
   */
  height?: number;
  /**
   * Show percentage text
   * @default false
   */
  showPercentage?: boolean;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Custom label instead of percentage
   */
  label?: string;
  /**
   * Current status of the progress
   */
  status?: 'pending' | 'processing' | 'completed';
}

/**
 * ProgressBar component for displaying progress
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  value = 0,
  height = 8,
  showPercentage = false,
  className = '',
  label,
  status,
}) => {
  // Ensure value is within bounds
  const clampedValue = Math.min(Math.max(value, 0), 100);

  // Generate status class
  const statusClass = status ? styles[`status-${status}`] : '';

  return (
      <div className={`${styles.container} ${className}`}>
          {(showPercentage || label) && (
          <div className={styles.labelContainer}>
              {label || `${clampedValue}%`}
          </div>
      )}
          <div
              className={`${styles.progressBarContainer} ${statusClass}`}
              style={{ height: `${height}px` }}
      >
              <div
                  className={styles.progressBar}
                  style={{ width: `${clampedValue}%` }}
                  aria-valuenow={clampedValue}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  role="progressbar"
        />
          </div>
      </div>
  );
};

export default ProgressBar;
